import './GraphSidePanel.css';

import type { GraphSidePanelContentId } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { memo } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { triggerAnalyticsEvent } from 'app/analytics/posthogEvents';
import { GraphSideTab } from 'components/Graph/GraphSidePanel/GraphSideTab';
import { closeSidePanel } from 'components/Graph/commands/closeSidePanel';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { NetworkViewerIcon } from 'components/Graph/icons/NetworkViewerIcon';
import { useLocale } from 'containers/App/LocaleContext';
import { useGraphContextSelectors } from '../GraphContext';
import { CompareSnapshotsIcon } from '../icons/CompareSnapshotsIcon';
import { IntentVerificationIcon } from '../icons/IntentVerificationIcon';
import { PathlookupIcon } from '../icons/PathlookupIcon';
import { GraphSideToggle } from './GraphSideToggle';

const BareGraphSidePanel = () => {
  const msg = useLocale();

  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    sidePanel: prop('sidePanel'),
  });

  const handleIconClick = useStableCallback(
    (tabId: GraphSidePanelContentId) => {
      if (ctxProps.sidePanel.open && tabId === ctxProps.sidePanel.contentId) {
        ctxProps.dispatch(closeSidePanel);
        triggerAnalyticsEventCloseSidePanel();
      } else {
        ctxProps.dispatch(showSidePanel, { contentId: tabId });
        triggerAnalyticsEventChangeSidePanel(
          tabId,
          ctxProps.sidePanel.contentId,
        );
      }
    },
  );

  return (
    <div className='graph-side-panel__icons flex flex-col justify-between'>
      <div>
        <GraphSideTab
          active={
            ctxProps.sidePanel.open &&
            ctxProps.sidePanel.contentId === 'networkViewer'
          }
          iconActive={
            <NetworkViewerIcon
              title={null}
              className='graph-side-panel__icon-colored'
              variant='coloredLight'
            />
          }
          iconHover={
            <NetworkViewerIcon
              title={null}
              className='graph-side-panel__icon-hover'
              variant='dark'
            />
          }
          icon={
            <NetworkViewerIcon
              title={null}
              className='graph-side-panel__icon-default'
              variant='dark'
            />
          }
          label={msg.canvasGraph.graphSidePanel.networkViewer.title}
          onIconClick={handleIconClick}
          tabId='networkViewer'
        />
        <GraphSideTab
          active={
            ctxProps.sidePanel.open &&
            ctxProps.sidePanel.contentId === 'pathlookup'
          }
          iconActive={
            <PathlookupIcon
              title={null}
              className='graph-side-panel__icon-colored'
              variant='coloredLight'
            />
          }
          iconHover={
            <PathlookupIcon
              title={null}
              className='graph-side-panel__icon-hover'
              variant='dark'
            />
          }
          icon={
            <PathlookupIcon
              title={null}
              className='graph-side-panel__icon-default'
              variant='dark'
            />
          }
          label={msg.canvasGraph.graphSidePanel.pathlookup.title}
          onIconClick={handleIconClick}
          tabId='pathlookup'
        />
        <GraphSideTab
          active={
            ctxProps.sidePanel.open &&
            ctxProps.sidePanel.contentId === 'intentVerification'
          }
          iconActive={
            <IntentVerificationIcon
              title={null}
              className='graph-side-panel__icon-colored'
              variant='colored'
            />
          }
          iconHover={
            <IntentVerificationIcon
              title={null}
              className='graph-side-panel__icon-hover'
              variant='dark'
            />
          }
          icon={
            <IntentVerificationIcon
              title={null}
              className='graph-side-panel__icon-default'
              variant='dark'
            />
          }
          label={msg.canvasGraph.graphSidePanel.intentVerification.title}
          onIconClick={handleIconClick}
          tabId='intentVerification'
        />
        <GraphSideTab
          active={
            ctxProps.sidePanel.open &&
            ctxProps.sidePanel.contentId === 'compareSnapshot'
          }
          iconActive={
            <CompareSnapshotsIcon
              title={null}
              className='graph-side-panel__icon-colored'
              variant='colored'
            />
          }
          iconHover={
            <CompareSnapshotsIcon
              title={null}
              className='graph-side-panel__icon-hover'
              variant='dark'
            />
          }
          icon={
            <CompareSnapshotsIcon
              title={null}
              className='graph-side-panel__icon-default'
              variant='dark'
            />
          }
          label={msg.canvasGraph.graphSidePanel.compareSnapshots.title}
          onIconClick={handleIconClick}
          tabId='compareSnapshot'
        />
      </div>
      <GraphSideToggle />
    </div>
  );
};

export const GraphSidePanel = memo(BareGraphSidePanel);

/* Analytic events */

const triggerAnalyticsEventCloseSidePanel = () => {
  triggerAnalyticsEvent({
    eventName: 'dialog',
    data: {
      dialog_name: 'graph_side_panel',
      action: 'close',
      type: 'side_panel',
      element: 'anchor_link',
      trigged_by: 'mouse_click',
    },
  });
};

const triggerAnalyticsEventChangeSidePanel = (
  newId: GraphSidePanelContentId,
  previousId: GraphSidePanelContentId,
) => {
  triggerAnalyticsEvent({
    eventName: 'navigation',
    data: {
      type: 'tab_change',
      previous_location: previousId,
      new_location: newId,
      element: 'vertical_tab',
      trigged_by: 'mouse_click',
    },
  });
};
